'use client';

import { ENGINE_PATH_PREFIX, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import * as ENGINE from '@gnsx/genesys.js';
import { isEmpty } from 'es-toolkit/compat';
import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { ComboboxGroup } from '@/components/ui/combobox';
import type { IAssetFile } from '@/editor/store/models/assetTree';
import type { Instance } from 'mobx-state-tree';

import { showAssetInAssetBrowser } from '@/editor/core/actions/assetBrowserReveal';
import { splitAssetPathForDisplay } from '@/editor/core/assetPathWarnings';
import { AssetType } from '@/editor/core/assetTypes';
import { assetsStore, editorStore } from '@/editor/store';


/** Model for asset reference property */
export const AssetProperty = t.compose('AssetProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Asset),
    value: t.optional(t.string, ''), // asset path/id
    defaultValue: t.optional(t.string, ''), // asset path/id
    assetTypes: t.array(t.enumeration('AssetType', Object.values(AssetType))),
    resourceType: t.maybe(t.string), // For filtering resources by class name
    preview: t.maybe(t.string), // preview url/thumbnail
  })
).views(self => ({
  get normalizedValue(): string {
    return self.value.startsWith('/@') ? self.value.slice(1) : self.value;
  },
  get isMissingAssetPath(): boolean {
    if (isEmpty(this.normalizedValue.trim())) return false;
    return !assetsStore.assetTree.findFile(this.normalizedValue);
  },
  get missingAssetMessage(): string | undefined {
    if (!this.isMissingAssetPath) return undefined;
    const { fileName, folderPath } = splitAssetPathForDisplay(this.normalizedValue);
    const loc = folderPath ? `${fileName} (${folderPath})` : fileName;
    return `Missing asset — ${self.label}: ${loc}`;
  },
  get selectOptions(): { label: string, value: string }[] {
    const assetTypes = self.assetTypes;
    return this.selectFiles.map(file => ({ label: getAssetLabel(file.path, assetTypes), value: file.path, asset: file }));
  },
  get selectFiles(): IAssetFile[] {
    return assetsStore.getAssetSelectFiles(self.assetTypes, self.resourceType);
  },
  get displayedFiles(): IAssetFile[] {
    return editorStore.inspectorPanel.showEngineAssets
      ? this.selectFiles
      : this.selectFiles.filter(file => !file.path.startsWith(ENGINE_PATH_PREFIX));
  },
  get displaySelectOptions(): { label: string, value: string }[] {
    const assetTypes = self.assetTypes;
    return this.displayedFiles.map(file => ({ label: getAssetLabel(file.path, assetTypes), value: file.path, asset: file }));
  },
  get groups(): ComboboxGroup[] {
    return groupAssetsByPath(this.selectFiles, self.assetTypes);
  },
  get displayGroups(): ComboboxGroup[] {
    return groupAssetsByPath(this.displayedFiles, self.assetTypes);
  },
  get displayValue(): string {
    const assetTypes = self.assetTypes;
    return self.value === '' ? '' : getAssetLabel(self.value, assetTypes);
  }
})).actions(self => ({
  afterCreate() {
    self.value = validateAssetPath(self.value);
  },
  setValue(value: string, fromPanel = true, initial = false) {
    if (value === self.value) return;
    value = validateAssetPath(value);
    self.value = value;
    self.notifyValueChange(self.path, value, { fromPanel, initial, property: self as Instance<typeof AssetProperty> });
  },
  setDefaultValue(value: string) {
    self.defaultValue = validateAssetPath(value);
  },
  setPreview(url: string) {
    self.preview = url;
  },
  showInAssetBrowser() {
    showAssetInAssetBrowser(self.value);
  }
}));


/** Normalizes asset path by adding @engine prefix if needed */
function validateAssetPath(path: string): string {
  if (!isEmpty(path.trim())) {
    if (path === 'none') {
      return '';
    }
    if (!path.startsWith('@') && !path.startsWith('/@')) {
      return path.startsWith('/') ? `@engine${path}` : `@engine/${path}`;
    }
  }
  return path;
}

function getAssetLabel(path: string, assetTypes: AssetType[]) {
  let label = path.split('/').pop() ?? path;
  if (assetTypes.includes(AssetType.Material)) {
    label = label.replace(ENGINE.MATERIAL_EXTENSION, '');
  }
  if (assetTypes.includes(AssetType.Prefab)) {
    label = label.replace(ENGINE.PREFAB_EXTENSION, '');
  }
  if (assetTypes.includes(AssetType.VFX)) {
    label = label.replace(ENGINE.VFX_EXTENSION, '');
  }
  return label;
}

function getAssetGroupLabel(path: string): string {
  const pathParts = path.split('/');
  if (pathParts.length <= 1) return 'Root';

  // Remove the filename to get the directory path
  const directoryPath = pathParts.slice(0, -1).join('/');

  return directoryPath;
}

function groupAssetsByPath(files: IAssetFile[], assetTypes: AssetType[]): ComboboxGroup[] {
  const groups = new Map<string, { label: string, value: string }[]>();

  for (const file of files) {
    const path = file.path;
    const groupLabel = getAssetGroupLabel(path);
    const option = { label: getAssetLabel(path, assetTypes), value: path, asset: file };

    if (!groups.has(groupLabel)) {
      groups.set(groupLabel, []);
    }
    groups.get(groupLabel)!.push(option);
  }

  // Convert to ComboboxGroup array and sort groups
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      if (a.startsWith(PROJECT_PATH_PREFIX)) return -1;
      if (b.startsWith(PROJECT_PATH_PREFIX)) return 1;
      if (a.startsWith(ENGINE_PATH_PREFIX)) return 1;
      if (b.startsWith(ENGINE_PATH_PREFIX)) return -1;
      return a.localeCompare(b);
    })
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label))
    }));
}